import { useEffect } from "react";

/* The two overlays that can cover the end of the page: the cookie consent
   banner and the promo popup. */
const PROMO_SEL = ".promo-banner";
const COOKIE_SEL = ".cookie-consent";
const BOTH_SEL = `${PROMO_SEL}, ${COOKIE_SEL}`;
const TOTAL_VAR = "--gs-bottom-bars-h";
const PROMO_VAR = "--gs-promo-h";

/**
 * Publishes how much of the bottom of the screen the fixed bars cover, so the
 * footer can reserve exactly that much room and its copyright line is never
 * buried underneath them.
 *
 * Why measure instead of hard-coding a number: the promo popup is much taller
 * than the cookie banner (measured 141-150px vs 68-143px depending on width and
 * text wrapping) and it only exists ~3s after load, so any fixed value is either
 * too small for some widths or leaves dead space for the rest. It also has no
 * stored "dismissed" state, so it reappears on every page load — this is the
 * normal first impression, not an edge case.
 *
 * `--gs-bottom-bars-h` is the total space the bars need, and `--gs-promo-h` is
 * the promo height on its own (the cookie banner is lifted above the promo on
 * mobile, so the two no longer sit on top of each other and the total becomes
 * the sum rather than the taller of the two).
 *
 * Sizes come from ResizeObserver notifications instead of getBoundingClientRect.
 * That is not a micro-optimisation: every bar used to be measured with a rect
 * read inside a MutationObserver callback, and because the callback also fired
 * for DOM changes that had nothing to do with the bars (React re-renders, the
 * analytics tag injecting its script), each read forced a full-document layout
 * of a page that carries ~290KB of inlined CSS — measured at 2 reads / 97ms of
 * main thread per page load, which DevTools and Lighthouse reported as a
 * "forced reflow" blamed on googletagmanager. ResizeObserver delivers the box
 * size after layout has already happened, so this hook now only writes custom
 * properties and never reads geometry.
 *
 * Safe for prerendering: useEffect does not run under react-snap, so the
 * properties stay unset and the stylesheet fallbacks apply.
 */
export default function useBottomBarsSpace(): void {
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const root = document.documentElement;
    const heights = new Map<Element, number>();

    const heightOf = (sel: string): number => {
      const el = document.querySelector(sel);
      return el ? heights.get(el) || 0 : 0;
    };

    function publish(): void {
      const promo = heightOf(PROMO_SEL);
      const cookie = heightOf(COOKIE_SEL);
      // When only one bar is on screen it is the only thing covering the page.
      // When both are up, the cookie banner is lifted above the promo on mobile
      // (see CookieConsentBanner.css), so they stack and the space needed is
      // the sum of the two.
      const total = promo > 0 && cookie > 0 ? promo + cookie : Math.max(promo, cookie);
      const set = (name: string, value: number) => {
        const next = value > 0 ? `${Math.ceil(value)}px` : "0px";
        if (root.style.getPropertyValue(name) !== next) {
          root.style.setProperty(name, next);
        }
      };
      set(TOTAL_VAR, total);
      set(PROMO_VAR, promo);
    }

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.borderBoxSize?.[0];
        heights.set(entry.target, box ? box.blockSize : entry.contentRect.height);
      }
      publish();
    });

    const watched = new WeakSet<Element>();
    const watch = (el: Element) => {
      if (watched.has(el)) return;
      watched.add(el);
      heights.set(el, 0);
      ro.observe(el);
    };

    const watchExisting = () => {
      document.querySelectorAll(BOTH_SEL).forEach(watch);
    };

    // A bar that leaves the DOM (either overlay can be dismissed) stops
    // reporting sizes, so drop it and let the remaining one take over.
    const forgetDetached = () => {
      heights.forEach((_height, el) => {
        if (!el.isConnected) {
          heights.delete(el);
          ro.unobserve(el);
        }
      });
    };

    watchExisting();
    publish();

    // Only needed to catch the bars being mounted/unmounted; their size changes
    // arrive through ResizeObserver.
    const mo = new MutationObserver((records) => {
      let touched = false;
      for (const record of records) {
        const target = record.target as Element;
        if (target.nodeType === 1 && (target.matches(BOTH_SEL) || target.closest(BOTH_SEL))) {
          touched = true;
          break;
        }
        for (let i = 0; i < record.addedNodes.length; i++) {
          const node = record.addedNodes[i];
          if (
            node.nodeType === 1 &&
            ((node as Element).matches(BOTH_SEL) || (node as Element).querySelector(BOTH_SEL))
          ) {
            touched = true;
            break;
          }
        }
        if (touched) break;
      }
      if (!touched) return;
      forgetDetached();
      watchExisting();
      publish();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      ro.disconnect();
      heights.clear();
      root.style.removeProperty(TOTAL_VAR);
      root.style.removeProperty(PROMO_VAR);
    };
  }, []);
}
